import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import {
  TREASURY_INSUFFICIENT_FUNDS_ACTIONS,
  TREASURY_MONTHS,
  TREASURY_REDEEM_FREQUENCIES,
  TREASURY_REDEEM_MAX_DAY,
  TREASURY_REDEEM_YEARLY,
} from '../../../../constants/treasury';
import { treasuryRedeemRuleFormFields as F } from '../../../../forms/fields/formFields/treasuryRedeemRule';
import { treasuryRedeemRuleSchema } from '../../../../forms/validationSchema/formSchema/treasuryRedeemRule';
import { SubscribedPortfolio } from '../../../../types/subscribedPortfolio';
import {
  SaveTreasuryRedeemAutomationRequest,
  TreasuryInsufficientFundsAction,
  TreasuryRedeemAutomation,
  TreasuryRedeemFrequency,
} from '../../../../types/treasury';
import { TreasuryPortfolioSelect } from './TreasuryPortfolioSelect';

type TreasuryRedeemRuleDialogProps = {
  open: boolean;
  /** The rule being edited; null creates a new one. */
  rule: TreasuryRedeemAutomation | null;
  portfolios: SubscribedPortfolio[];
  isPortfoliosLoading: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: SaveTreasuryRedeemAutomationRequest) => void;
};

type RuleFormValues = {
  [F.portfolioId]: number | '';
  [F.amount]: string;
  [F.frequency]: TreasuryRedeemFrequency;
  [F.dayOfMonth]: number;
  [F.monthOfYear]: number;
  [F.insufficientFundsAction]: TreasuryInsufficientFundsAction;
};

const DAYS = Array.from({ length: TREASURY_REDEEM_MAX_DAY }, (_, index) => index + 1);

/**
 * The form, mounted per open — it starts from the rule (edit) or the defaults (create). Save stays open on an unchanged
 * rule: saving it again is how a rule waiting for manual review is cleared.
 */
const TreasuryRedeemRuleForm = ({
  rule,
  portfolios,
  isPortfoliosLoading,
  isSaving,
  onClose,
  onSave,
}: Omit<TreasuryRedeemRuleDialogProps, 'open'>) => {
  const { handleSubmit, values, handleChange, handleBlur, setFieldValue, touched, errors, isValid } =
    useFormik<RuleFormValues>({
      initialValues: {
        [F.portfolioId]: rule?.externalSubscribedPortfolioId ?? '',
        [F.amount]: rule ? String(rule.amount) : '',
        [F.frequency]: rule?.frequency ?? 'MONTHLY',
        [F.dayOfMonth]: rule?.dayOfMonth ?? 1,
        [F.monthOfYear]: rule?.monthOfYear ?? 1,
        [F.insufficientFundsAction]: rule?.insufficientFundsAction ?? 'REDEEM_AVAILABLE',
      },
      validationSchema: treasuryRedeemRuleSchema,
      validateOnMount: true,
      onSubmit: (formValues) => {
        const portfolioId = formValues[F.portfolioId];

        if (portfolioId === '') return;

        onSave({
          externalSubscribedPortfolioId: portfolioId,
          amount: Number(formValues[F.amount]),
          frequency: formValues[F.frequency],
          dayOfMonth: formValues[F.dayOfMonth],
          // thriftplan keeps month_of_year for YEARLY only; null clears it when a rule stops being yearly.
          monthOfYear: formValues[F.frequency] === TREASURY_REDEEM_YEARLY ? formValues[F.monthOfYear] : null,
          insufficientFundsAction: formValues[F.insufficientFundsAction],
        });
      },
    });

  const isYearly = values[F.frequency] === TREASURY_REDEEM_YEARLY;
  const amountError = touched[F.amount] && errors[F.amount];

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>{rule ? `Edit Rule #${rule.id}` : 'New Redemption Rule'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TreasuryPortfolioSelect
              portfolios={portfolios}
              value={values[F.portfolioId]}
              onChange={(id) => setFieldValue(F.portfolioId, id)}
              isLoading={isPortfoliosLoading}
              disabled={Boolean(rule)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              id={F.amount}
              name={F.amount}
              label="Amount per firing"
              type="number"
              value={values[F.amount]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(amountError)}
              helperText={amountError}
              InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              id={F.frequency}
              name={F.frequency}
              label="Frequency"
              value={values[F.frequency]}
              onChange={handleChange}
            >
              {TREASURY_REDEEM_FREQUENCIES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={isYearly ? 6 : 12}>
            <TextField
              select
              fullWidth
              size="small"
              id={F.dayOfMonth}
              name={F.dayOfMonth}
              label="Day of the month"
              value={values[F.dayOfMonth]}
              onChange={(event) => setFieldValue(F.dayOfMonth, Number(event.target.value))}
              helperText={`1–${TREASURY_REDEEM_MAX_DAY}, so every month has it (Riyadh time).`}
            >
              {DAYS.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {isYearly && (
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                id={F.monthOfYear}
                name={F.monthOfYear}
                label="Month"
                value={values[F.monthOfYear]}
                onChange={(event) => setFieldValue(F.monthOfYear, Number(event.target.value))}
              >
                {TREASURY_MONTHS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              id={F.insufficientFundsAction}
              name={F.insufficientFundsAction}
              label="When the portfolio is worth less than the amount"
              value={values[F.insufficientFundsAction]}
              onChange={handleChange}
              helperText={
                TREASURY_INSUFFICIENT_FUNDS_ACTIONS.find((option) => option.value === values[F.insufficientFundsAction])
                  ?.description
              }
            >
              {TREASURY_INSUFFICIENT_FUNDS_ACTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!isValid || isSaving}>
          {rule ? 'Save' : 'Create Rule'}
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

/** Create or edit a redemption rule. The portfolio is fixed once created — delete and create a new rule instead. */
export const TreasuryRedeemRuleDialog = ({ open, ...props }: TreasuryRedeemRuleDialogProps) => (
  <Dialog open={open} onClose={props.onClose} maxWidth="sm" fullWidth>
    {open && <TreasuryRedeemRuleForm {...props} />}
  </Dialog>
);
